import type {
	PersistedClient,
	Persister,
} from '@tanstack/react-query-persist-client';
import { IDBStorage } from 'use-idb-storage';
import { isSSR } from './index';

export interface IDBPersisterOptions {
	database?: string;
	store?: string;
	key: IDBValidKey;
}

function queries({ database, store }: { database: string; store: string }) {
	return isSSR
		? undefined
		: new IDBStorage({
				database,
				store,
			}).store;
}

function safeSerialize(data: any): any {
	try {
		return JSON.parse(
			JSON.stringify(data, (_key, value) => {
				if (typeof value === 'function') return undefined;
				if (value instanceof Promise) return undefined;
				if (value instanceof Element) return undefined;
				if (value instanceof Date) {
					return { __type: 'Date', value: value.toISOString() };
				}
				if (
					value &&
					typeof value === 'object' &&
					value.constructor !== Object &&
					value.constructor !== Array &&
					!(value instanceof Date)
				) {
					return undefined;
				}

				return value;
			}),
		);
	} catch (error) {
		console.warn('Failed to serialize data for IndexedDB:', error);
		return null;
	}
}

function safeDeserialize(data: any): any {
	if (!data) return null;

	try {
		return JSON.parse(JSON.stringify(data), (_key, value) => {
			if (value && typeof value === 'object' && value.__type === 'Date') {
				return new Date(value.value);
			}
			return value;
		});
	} catch (error) {
		console.warn('Failed to deserialize data from IndexedDB:', error);
		return null;
	}
}

export function createIDBPersister({
	database = 'sohanscript',
	store = 'queries',
	key,
}: IDBPersisterOptions) {
	const idbQueries = queries({ database, store });

	return {
		persistClient: async (client: PersistedClient) => {
			try {
				const safeClient = safeSerialize(client);

				if (safeClient) {
					await (await idbQueries)?.set(String(key), safeClient).catch(() => {
						// IndexedDB store may reject during schema migrations or
						// contention with other IDB users (e.g. PostHog/Dexie).
						// This is non-critical — query data will be repopulated on next fetch.
					});
				} else {
					console.warn(
						'Skipping persistence - client contains non-serializable data',
					);
				}
			} catch (error) {
				console.error('Failed to persist client to IndexedDB:', error);
			}
		},
		restoreClient: async () => {
			try {
				const stored = await (await idbQueries)?.get<PersistedClient>(
					String(key),
				);
				return stored ? safeDeserialize(stored) : undefined;
			} catch (error) {
				console.error('Failed to restore client from IndexedDB:', error);
				return undefined;
			}
		},
		removeClient: async () => {
			try {
				await (await idbQueries)?.delete(String(key));
			} catch (error) {
				console.error('Failed to remove client from IndexedDB:', error);
			}
		},
	} as Persister;
}
